import type { Sprite, Texture } from 'pixi.js'

import type { ResolvedTheme } from '../theme'

/**
 * Placing themed art inside a cell.
 *
 * Shared by both renderers, because "how big is this picture in its cell"
 * must not be answered twice: a symbol that reads right on the tumble
 * board and squashed on the reels is the kind of bug nobody files, they
 * just say the game looks cheap.
 */

/**
 * Size art for a cell.
 *
 * `contain` fits the longer side and keeps the aspect ratio — the default,
 * because a designer's 512×640 symbol must not become square. `stretch`
 * fills the cell exactly and is only right for art drawn square on purpose.
 * Padding is a fraction of the cell: art usually needs air around it.
 *
 * `scale` is the symbol's own multiplier on that box — how a scatter says
 * "bigger than the rest" without smuggling the decision into its PNG
 * padding. It multiplies the box, so padding and fit still apply.
 */
export function fitArt(
  sprite: Sprite,
  texture: Texture,
  cell: number,
  theme: ResolvedTheme,
  scale = 1,
): void {
  const box = cell * (1 - 2 * theme.board.symbolPadding) * scale

  if (theme.board.symbolFit === 'stretch' || !texture.width || !texture.height) {
    sprite.width = box
    sprite.height = box
    return
  }

  const fit = Math.min(box / texture.width, box / texture.height)
  sprite.width = texture.width * fit
  sprite.height = texture.height * fit
}

/**
 * Size a symbol's own backdrop: the full cell, always.
 *
 * Stretch rather than contain, and no padding: the backdrop IS the tile —
 * its shape and rounding are drawn inside the file, so any gap around it
 * would read as a rendering seam. The character layer on top is the one
 * that keeps its aspect ratio (see `fitArt`).
 */
export function fillCell(sprite: Sprite, cell: number): void {
  sprite.width = cell
  sprite.height = cell
}

/**
 * Size and place frame art so its opening lands on the board.
 *
 * `coverBoard` cannot do this: cover pins the art to the board rect, so a
 * surround drawn around an opening ends up sitting ON the outer cells. And
 * scaling each axis to hit the opening exactly would distort the drawing —
 * the columns of a gate stretch, which is precisely what makes frame art
 * look wrong.
 *
 * So the scale stays uniform and is chosen to make the opening COVER the
 * board, then the opening is centred on it. The board is usually a little
 * smaller than the opening on one axis; that slack reads as air inside the
 * frame, which is what a frame looks like.
 *
 * Insets are per side because an opening is rarely centred in its art: a
 * roof is deeper than the sill below it.
 */
export function fitFrame(
  sprite: Sprite,
  texture: Texture,
  width: number,
  height: number,
  inset: { left: number; right: number; top: number; bottom: number } | null,
): void {
  if (!inset || !texture.width || !texture.height) {
    coverBoard(sprite, texture, width, height)
    return
  }
  const openW = 1 - inset.left - inset.right
  const openH = 1 - inset.top - inset.bottom
  const aspect = texture.height / texture.width

  // Widen until the opening covers the board on both axes, never stretching:
  // whichever axis needs more wins, and the other gains slack.
  const frameW = Math.max(width / openW, (height / openH) / aspect)
  const frameH = frameW * aspect
  sprite.width = frameW
  sprite.height = frameH

  // The sprite is anchored at its own centre, but the opening's centre is
  // what has to sit on the board's — hence the shift by half the imbalance.
  sprite.x = width / 2 + ((inset.right - inset.left) / 2) * frameW
  sprite.y = height / 2 + ((inset.bottom - inset.top) / 2) * frameH
}

/**
 * Size a backdrop or frame to cover the board.
 *
 * Cover rather than contain: a backdrop with bars down the sides is worse
 * than one cropped a little, and frame art is authored for the board's
 * proportions anyway.
 */
export function coverBoard(
  sprite: Sprite,
  texture: Texture,
  width: number,
  height: number,
): void {
  if (!texture.width || !texture.height) {
    sprite.width = width
    sprite.height = height
    return
  }
  const scale = Math.max(width / texture.width, height / texture.height)
  sprite.width = texture.width * scale
  sprite.height = texture.height * scale
  sprite.x = width / 2
  sprite.y = height / 2
}
